import React from "react";
import { Loader2, Inbox, ImageIcon, Camera, X } from "lucide-react";
import { compressImage } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function Spinner({ className = "" }) {
  return <Loader2 className={`animate-spin ${className}`} />;
}

export function Loading({ label = "Memuat..." }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
      <Loader2 className="animate-spin h-8 w-8 mb-3 text-primary" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function EmptyState({ title = "Belum ada data", subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center mb-4">
        <Inbox className="h-7 w-7 text-muted-foreground" />
      </div>
      <p className="font-heading font-medium text-foreground">{title}</p>
      {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{subtitle}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Field({ label, children, hint, required }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

// Image upload with compression -> returns base64 via onChange
export function ImageUpload({ value, onChange, label = "Foto", testId = "image-upload" }) {
  const libRef = React.useRef();
  const camRef = React.useRef();
  const [busy, setBusy] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [error, setError] = React.useState("");

  const handle = async (e) => {
    const file = e.target.files && e.target.files[0];
    // reset input so re-selecting the same file re-triggers change
    e.target.value = "";
    if (!file || busy) return; // busy guard prevents duplicate processing
    setError("");
    setBusy(true);
    setProgress(5);
    try {
      const b64 = await compressImage(file, { onProgress: setProgress });
      onChange(b64);
    } catch (err) {
      setError(err?.message || "Gagal mengunggah gambar. Coba format JPG/PNG.");
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          {value ? (
            <img src={value} alt="preview" className="h-20 w-20 rounded-xl object-cover border border-border" data-testid={`${testId}-preview`} />
          ) : (
            <div className="h-20 w-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center text-xs text-muted-foreground text-center px-1">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : label}
            </div>
          )}
          {busy && (
            <div className="absolute inset-0 rounded-xl bg-background/70 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 min-w-0">
          {/* Library / files picker: NO capture attribute -> opens Photo Library + Files on iOS */}
          <input ref={libRef} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={handle} data-testid={testId} />
          {/* Camera: capture forces the camera */}
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handle} data-testid={`${testId}-camera`} />

          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={() => libRef.current?.click()} data-testid={`${testId}-choose`}
              className="h-9 px-3 rounded-lg border border-border bg-card text-sm hover:bg-secondary/60 inline-flex items-center gap-1.5 disabled:opacity-50">
              <ImageIcon className="h-3.5 w-3.5" />{value ? "Pilih dari Galeri" : "Pilih dari Galeri"}
            </button>
            <button type="button" disabled={busy} onClick={() => camRef.current?.click()} data-testid={`${testId}-take`}
              className="h-9 px-3 rounded-lg border border-border bg-card text-sm hover:bg-secondary/60 inline-flex items-center gap-1.5 disabled:opacity-50">
              <Camera className="h-3.5 w-3.5" />Ambil Foto
            </button>
            {value && (
              <button type="button" disabled={busy} onClick={() => { setError(""); onChange(null); }} data-testid={`${testId}-remove`}
                className="h-9 px-3 rounded-lg border border-border bg-card text-sm text-destructive hover:bg-destructive/10 inline-flex items-center gap-1.5 disabled:opacity-50">
                <X className="h-3.5 w-3.5" />Hapus Foto
              </button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">JPG, PNG, WEBP, HEIC/HEIF · maks. otomatis dikompres</p>
          {busy && (
            <div className="h-1.5 w-40 rounded-full bg-secondary overflow-hidden" data-testid={`${testId}-progress`}>
              <div className="h-full bg-primary transition-[width] duration-200" style={{ width: `${progress}%` }} />
            </div>
          )}
          {error && <p className="text-[11px] text-destructive" data-testid={`${testId}-error`}>{error}</p>}
        </div>
      </div>
    </div>
  );
}


// ---- Skeleton loaders (match final layout for smooth perceived loading) ----
export function SkeletonCardGrid({ count = 8, className = "" }) {
  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 ${className}`} data-testid="skeleton-card-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-card overflow-hidden">
          <Skeleton className="aspect-square w-full rounded-none" />
          <div className="p-3 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <div className="flex justify-between pt-1">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-12" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 8, className = "" }) {
  return (
    <div className={`rounded-xl border border-border bg-card overflow-hidden ${className}`} data-testid="skeleton-table">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3.5 border-b border-border last:border-0">
          <Skeleton className="h-9 w-9 rounded-full shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-1/3" />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <Skeleton className="h-4 w-20 hidden sm:block" />
          <Skeleton className="h-8 w-16 rounded-md" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonDetail({ className = "" }) {
  return (
    <div className={`space-y-4 ${className}`} data-testid="skeleton-detail">
      <Skeleton className="aspect-square w-full rounded-xl" />
      <Skeleton className="h-6 w-2/3" />
      <div className="flex gap-2"><Skeleton className="h-5 w-16 rounded-full" /><Skeleton className="h-5 w-20 rounded-full" /></div>
      <div className="space-y-2">
        <Skeleton className="h-3.5 w-full" /><Skeleton className="h-3.5 w-full" /><Skeleton className="h-3.5 w-4/5" /><Skeleton className="h-3.5 w-3/5" />
      </div>
    </div>
  );
}
